import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { allComics, categories } from '@/mocks/comics';
import { testimonials, faqItems } from '@/mocks/testimonials';
import { useState } from 'react';

export default function ArticlePage() {
  const { articleId } = useParams<{ articleId: string }>();
  const id = parseInt(articleId || '0', 10);
  const comic = allComics.find((c) => c.id === id);

  const [faqOpen, setFaqOpen] = useState<number | null>(null);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-20 text-center">
            <h1 className="text-2xl font-heading text-foreground-900 mb-4">漫画不存在</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  const categoryId = categories.find((c) => c.name === comic.category)?.id || 'fantasy';

  const breadcrumbListLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'http://jorjigsrp.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: comic.category,
        item: `http://jorjigsrp.com/category/${categoryId}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: comic.title,
        item: `http://jorjigsrp.com/article/${comic.id}`,
      },
    ],
  };

  const relatedComics = allComics.filter((c) => c.id !== comic.id).slice(0, 6);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbListLd),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: `${comic.title} - ${comic.subtitle}`,
            description: comic.description,
            author: { '@type': 'Person', name: comic.author },
            datePublished: '2026-01-15',
            dateModified: comic.updatedAt,
            url: `http://jorjigsrp.com/article/${comic.id}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '韩漫大全',
              url: 'http://jorjigsrp.com/',
            },
          }),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <article>
            <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8 md:py-12">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: comic.category, href: `/category/${categoryId}` },
                  { label: comic.title },
                ]}
              />

              <div className="flex flex-col lg:flex-row gap-10">
                <div className="lg:w-[40%]">
                  <span className="inline-block px-3 py-1 text-xs font-medium border border-foreground-200/70 rounded-full text-foreground-600 mb-6 whitespace-nowrap">
                    {comic.status}
                  </span>

                  <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
                    <span className="font-bold">{comic.title}</span>
                  </h1>
                  <p className="text-lg font-heading text-foreground-500 mb-8">
                    {comic.subtitle}
                  </p>

                  <p className="text-sm text-foreground-600 leading-relaxed mb-8 max-w-md">
                    {comic.description}
                  </p>

                  <div className="flex items-center gap-3 mb-8">
                    {comic.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center gap-8 mb-8">
                    <div>
                      <span className="block text-2xl font-bold text-foreground-900">{comic.chapters}</span>
                      <span className="text-xs text-foreground-500">总章节</span>
                    </div>
                    <div>
                      <span className="block text-2xl font-bold text-foreground-900">{formatViews(comic.views)}</span>
                      <span className="text-xs text-foreground-500">阅读量</span>
                    </div>
                    <div>
                      <span className="block text-2xl font-bold text-accent-500">{comic.rating}</span>
                      <span className="text-xs text-foreground-500">评分</span>
                    </div>
                  </div>

                  <a
                    href="#"
                    className="inline-flex items-center gap-2 bg-primary-500 text-background-50 dark:text-foreground-950 px-7 py-3 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap group"
                  >
                    开始阅读
                    <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                  </a>
                </div>

                <div className="lg:w-[60%] flex gap-4">
                  <div className="w-[45%] aspect-[3/4] rounded-lg overflow-hidden">
                    <img
                      src={comic.coverUrl}
                      alt={`${comic.title} 封面`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="w-[55%]">
                    <div className="aspect-[4/5] rounded-lg overflow-hidden">
                      <img
                        src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20vertical%20scroll%20reading%20preview%2C%20comic%20panels%20in%20warm%20tones%2C%20digital%20manhwa%20style%20with%20clean%20line%20art%2C%20soft%20shading%2C%20dramatic%20storytelling%20composition%2C%20high%20quality%20illustration&width=800&height=1200&seq=article-reading-preview&orientation=portrait"
                        alt="漫画预览"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </article>

          <section className="flex flex-col lg:flex-row">
            <div className="lg:w-1/2 relative h-[250px] md:h-[350px] bg-secondary-100 overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20vertical%20scroll%20mobile%20reading%20display%2C%20digital%20comic%20on%20smartphone%20screen%2C%20warm%20cream%20background%2C%20clean%20modern%20interface%2C%20soft%20lighting%2C%20minimalist%20composition&width=1200&height=800&seq=article-scroll-preview&orientation=landscape"
                alt="下拉式阅读体验"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-secondary-100/50 to-transparent"></div>
              <div className="absolute top-6 left-6">
                <span className="text-xs font-light text-foreground-700 whitespace-nowrap">下拉式阅读 · 流畅体验</span>
              </div>
              <div className="absolute bottom-6 left-6">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  下拉式<br />阅读体验
                </h2>
              </div>
            </div>

            <div className="lg:w-1/2 bg-background-50 flex items-center justify-center py-10 md:py-14 px-6 md:px-12">
              <div className="max-w-md text-center">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-4">
                  开始阅读《{comic.title}》
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-7">
                  下拉式阅读，无需翻页，向下滑动即可畅享完整无删减内容。随时随地，沉浸式韩国漫画阅读体验。
                </p>
                <a
                  href="#"
                  className="inline-flex items-center gap-2 bg-primary-500 text-background-50 dark:text-foreground-950 px-7 py-3 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap group"
                >
                  立即阅读
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                </a>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-light text-foreground-900 mb-3">
                  猜你喜欢
                </h2>
                <p className="text-sm text-foreground-500">
                  根据你的阅读偏好，推荐以下韩国漫画
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                {relatedComics.map((c, index) => (
                  <ComicCard
                    key={c.id}
                    {...c}
                    isFirst={index === 0}
                  />
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-secondary-50">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <span className="w-2 h-2 rounded-full bg-accent-500"></span>
                  <span className="text-xs font-medium text-foreground-600 whitespace-nowrap">读者评价</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  读者怎么说
                </h2>
                <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                  看看其他韩漫爱好者的真实阅读体验
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {testimonials.slice(0, 3).map((t) => (
                  <article key={t.id} className="bg-background-50 rounded-lg p-6">
                    <div className="flex items-center gap-1 mb-4">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-sm ${i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                        ></i>
                      ))}
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2">{t.title}</h3>
                    <p className="text-xs text-foreground-600 leading-relaxed mb-5">{t.content}</p>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full overflow-hidden bg-secondary-200 flex-shrink-0">
                        <img src={t.avatarUrl} alt={t.name} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-foreground-900 whitespace-nowrap">{t.name}</p>
                        <p className="text-[10px] text-foreground-500 whitespace-nowrap">{t.role}</p>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  常见问题
                </h2>
                <p className="text-sm text-foreground-500">关于韩漫大全的常见疑问解答</p>
              </div>

              <div className="max-w-3xl mx-auto space-y-3">
                {faqItems.map((item, index) => (
                  <div key={index} className="bg-background-100 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setFaqOpen(faqOpen === index ? null : index)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left"
                    >
                      <span className="text-sm font-medium text-foreground-900 pr-4">{item.question}</span>
                      <i className={`${faqOpen === index ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-500 text-sm flex-shrink-0 transition-transform duration-200`}></i>
                    </button>
                    {faqOpen === index && (
                      <div className="px-5 pb-4">
                        <p className="text-xs text-foreground-600 leading-relaxed">{item.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}